import BaseService from "react-lib/apis/services/BaseService";

import { normalizePurRecord, PurRecord } from "./purApi";
import { canUsePurDraftCreatePilot } from "./purDraftCreate";
import { PurDraftKind } from "./purDraftCreatePilot";
import { getPur, PUR_REQUEST_TIMEOUT_MS, PurClientConfig, purRequestOptions, readId, readObject } from "./purRead";

export type PurApprovalAction = "SUBMIT" | "APPROVE";
export interface PurApprovalPermission { resource: boolean; submit: boolean; approve: boolean }
export interface PurApprovalAttempt {
  kind: PurDraftKind;
  action: PurApprovalAction;
  actorUserId: number;
  record: PurRecord;
}
export class PurApprovalError extends Error {
  constructor(public outcome: "blocked" | "rejected" | "unknown", public messageId: string) {
    super(messageId);
    Object.setPrototypeOf(this, PurApprovalError.prototype);
  }
}

const path = (kind: PurDraftKind): string => kind === "materialRequests" ? "material-requests" : "purchase-orders";
const model = (kind: PurDraftKind): string => kind === "materialRequests" ? "MaterialRequest" : "PurchaseOrder";
export const purApprovalPermissionNames = (kind: PurDraftKind) => ({
  resource: `users.UPDATE_PUR:${kind === "materialRequests" ? "material-request-detail" : "purchase-order-detail"}`,
  submit: `users.ACTION_PUR.${model(kind)}:SUBMIT`,
  approve: `users.ACTION_PUR.${model(kind)}:APPROVE`,
});
export const purApprovalNextAction = (kind: PurDraftKind, status: string): PurApprovalAction | null =>
  status === "DRAFT" ? "SUBMIT" : status === (kind === "materialRequests" ? "SUBMITTED" : "PENDING_APPROVAL") ? "APPROVE" : null;
export const purApprovalTargetStatus = (kind: PurDraftKind, action: PurApprovalAction): string =>
  action === "SUBMIT" ? kind === "materialRequests" ? "SUBMITTED" : "PENDING_APPROVAL" : "APPROVED";
const block = (id = "MEERP_APPROVAL_SCOPE_ERROR"): never => { throw new PurApprovalError("blocked", id); };

export const canUsePurApprovalPilot = (client: PurClientConfig, kind: PurDraftKind, record: PurRecord): boolean =>
  ["materialRequests", "purchaseOrders"].includes(kind) && canUsePurDraftCreatePilot(client)
  && readId(record.id) !== null && readId(record.projectId) !== null && readId(record.storageId) !== null
  && (kind === "materialRequests" ? readId(record.requestingDivisionId) === readId(client.divisionId) : readId(record.vendorId) !== null);

export const checkPurApprovalPermissions = async (client: PurClientConfig, kind: PurDraftKind): Promise<PurApprovalPermission> => {
  if (!canUsePurDraftCreatePilot(client)) return block();
  const names = purApprovalPermissionNames(kind);
  const keys = { resource: `perm_${names.resource.replace(".", "_")}`,
    submit: `action_PUR_${model(kind)}_SUBMIT`, approve: `action_PUR_${model(kind)}_APPROVE` };
  const { url, headers } = purRequestOptions({ ...client, pathPrefix: "/users/apis" }, "user-permission/");
  // This POST only queries the current session's effective permissions.
  const response = await new BaseService().client.post(url,
    { [keys.resource]: false, [keys.submit]: false, [keys.approve]: false }, { headers, timeout: PUR_REQUEST_TIMEOUT_MS });
  const data = readObject(response.data);
  return { resource: data[keys.resource] === true, submit: data[keys.submit] === true, approve: data[keys.approve] === true };
};

const readScopedRecord = async (client: PurClientConfig, kind: PurDraftKind, id: number): Promise<PurRecord> => {
  if (!canUsePurDraftCreatePilot(client) || readId(id) === null) return block();
  const record = normalizePurRecord(kind, await getPur(client, `${path(kind)}/${id}/`));
  if (record.id !== id || !canUsePurApprovalPilot(client, kind, record)) return block();
  const project = readObject(await getPur(client, `projects/${record.projectId}/`));
  if (readId(project.id) !== record.projectId || readId(project.division) !== readId(client.divisionId) || project.active !== true) return block();
  if (kind === "purchaseOrders") {
    const vendor = readObject(await getPur(client, `vendors/${record.vendorId}/`));
    if (readId(vendor.id) !== record.vendorId) return block();
  }
  return record;
};
const ensurePermission = async (client: PurClientConfig, kind: PurDraftKind, action: PurApprovalAction): Promise<void> => {
  const permission = await checkPurApprovalPermissions(client, kind);
  if (!permission.resource || !permission[action === "SUBMIT" ? "submit" : "approve"]) return block("MEERP_APPROVAL_PERMISSION_MISSING");
};

export const preparePurApproval = async (
  client: PurClientConfig, kind: PurDraftKind, id: number, action: PurApprovalAction,
): Promise<PurApprovalAttempt> => {
  if (!["materialRequests", "purchaseOrders"].includes(kind) || (action !== "SUBMIT" && action !== "APPROVE")) return block("MEERP_APPROVAL_STATE_CHANGED");
  await ensurePermission(client, kind, action);
  const profile = readObject(await getPur({ ...client, pathPrefix: "/users/apis" }, "users/profile/"));
  const actorUserId = readId(profile.user);
  if (actorUserId === null) return block("MEERP_APPROVAL_IDENTITY_ERROR");
  const record = await readScopedRecord(client, kind, id);
  if (purApprovalNextAction(kind, record.status) !== action || !record.items.length) return block("MEERP_APPROVAL_STATE_CHANGED");
  return { kind, action, actorUserId, record };
};

export const reconcilePurApproval = async (client: PurClientConfig, attempt: PurApprovalAttempt): Promise<PurRecord> =>
  readScopedRecord(client, attempt.kind, attempt.record.id);

export const performPurApproval = async (
  client: PurClientConfig, attempt: PurApprovalAttempt, isCurrent: () => boolean = () => true,
): Promise<PurRecord> => {
  let fresh: PurApprovalAttempt;
  try {
    fresh = await preparePurApproval(client, attempt.kind, attempt.record.id, attempt.action);
  } catch (error: unknown) {
    // No PATCH has started. A failed permission/profile/fixture read is safe
    // to retry after a new review, unlike an ambiguous mutation response.
    if (error instanceof PurApprovalError) throw error;
    throw new PurApprovalError("blocked", "MEERP_APPROVAL_PREFLIGHT_ERROR");
  }
  if (!isCurrent() || fresh.actorUserId !== attempt.actorUserId) return block("MEERP_APPROVAL_CONTEXT_CHANGED");
  // Refuse a same-status document edited after the user reviewed it as well.
  if (JSON.stringify(fresh.record) !== JSON.stringify(attempt.record)) return block("MEERP_APPROVAL_STATE_CHANGED");
  const { url, headers } = purRequestOptions(client, `${path(attempt.kind)}/${attempt.record.id}/`);
  try {
    // Only the action is writable here; never PATCH status, header data or items.
    const response = await new BaseService().client.patch(url, { action: attempt.action }, { headers, timeout: PUR_REQUEST_TIMEOUT_MS });
    const record = normalizePurRecord(attempt.kind, response.data);
    if (response.status !== 200 || record.id !== attempt.record.id || record.status !== purApprovalTargetStatus(attempt.kind, attempt.action)) {
      throw new PurApprovalError("unknown", "MEERP_APPROVAL_UNKNOWN");
    }
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    if (status && [400, 401, 403, 404, 405, 409, 422].includes(status)) {
      throw new PurApprovalError("rejected", status === 401 || status === 403 ? "MEERP_APPROVAL_PERMISSION_MISSING" : "MEERP_APPROVAL_REJECTED");
    }
    throw new PurApprovalError("unknown", "MEERP_APPROVAL_UNKNOWN");
  }
  try {
    const record = await reconcilePurApproval(client, attempt);
    if (record.status !== purApprovalTargetStatus(attempt.kind, attempt.action)) throw new Error("Unexpected PUR state");
    return record;
  } catch { throw new PurApprovalError("unknown", "MEERP_APPROVAL_UNKNOWN"); }
};
